import { Component, DestroyRef, EventEmitter, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ChildActivationEnd, ChildActivationStart, Router, UrlSegment } from '@angular/router';
import { Observable, Subscription, filter, first, of, switchMap } from 'rxjs';
                                                                                      
import { HoldingType } from 'src/app/shared/enums/HoldingType.enum';
import { InvestmentSecurityType } from 'src/app/shared/enums/InvestmentSecurityType.enum';
import { InvestmentType } from 'src/app/shared/enums/InvestmentType.enum';
import { GpAssetService } from '../gp-asset.service';

export enum AssetDraftTab {
  Overview = 0,
  Reports = 1,
  Documents = 2,
  Fundraisings = 3,
  Distributions = 4
}
interface TabInfo {
  label: string;
  // Make sure to keep it thin accessor, i.e. no complex computations, since it's being called with every change detection cycle
  labelBadge?: () => string;
  data?: {
    [key: string]: any;
  };
  route: string;
  index: AssetDraftTab;
  isHidden?: boolean;
  isActive?: boolean;
}

@Component({
  selector: 'terra-asset-draft-status',
  templateUrl: './asset-draft-status.component.html',
  styleUrls: ['./asset-draft-status.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush, // can't use because it prevents the initial tab to be selected when the component loads.
})
export class AssetDraftStatusComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  tabs: TabInfo[] = [
    { label: 'Overview', get labelBadge() { return this.data?.recordCount; }, route: 'overview', index: AssetDraftTab.Overview },
    { label: 'Reports', route: 'reports', index: AssetDraftTab.Reports },
    { label: 'Documents', route: 'documents', index: AssetDraftTab.Documents },
    { label: 'Contributions', route: 'contributions', index: AssetDraftTab.Fundraisings },
    { label: 'Distributions', route: 'distributions', index: AssetDraftTab.Distributions },
  ];

  private childTabDataSub?: Subscription;

  asset$ = this.gpAssetService.holding$;

  // Enums (used in the template)
  HoldingType = HoldingType;
  InvestmentType = InvestmentType;
  InvestmentSecurityType = InvestmentSecurityType;

  constructor(
    private gpAssetService: GpAssetService,
    private route: ActivatedRoute,
    private router: Router) { }

  ngOnInit() {
    // Activates the child tab based on the route once the component loads
    this.route.firstChild.url
      .pipe(first())
      .subscribe(urls => { this.onTabChanged(urls.length ? urls[0].path : "") });

    // Reacts to navigation between child tabs
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event): event is ChildActivationStart => event instanceof ChildActivationStart),
        switchMap(
          (): Observable<UrlSegment[]> => (this.route.firstChild && this.route.firstChild.url) || of([] as UrlSegment[]),
        ),
      )
      .subscribe(urls => { this.onTabChanging(urls.length ? urls[0].path : "") });
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event): event is ChildActivationEnd => event instanceof ChildActivationEnd),
        switchMap(
          (): Observable<UrlSegment[]> => (this.route.firstChild && this.route.firstChild.url) || of([] as UrlSegment[]),
        ),
      )
      .subscribe(urls => { this.onTabChanged(urls.length ? urls[0].path : "") });
  }

  /**
   * Mark tab with requested path as inactive and clears it's cached data.
   * @param {string} oldPath - The route path of the disactivated tab.
   * @returns {void}
   */
  onTabChanging(oldPath: string): void {
    const oldTab = this.tabs.find(t => t.route == oldPath);
    if (oldTab === undefined) {
      return;
    }
    oldTab.data = null;
    oldTab.isActive = false;
  }

  /**
   * Marks tab with requested path as active.
   * @param {string} newPath - The route path of the activated tab.
   * @returns {void}
   */
  onTabChanged(newPath: string): void {
    const newTab = this.tabs.find(t => t.route == newPath);
    if (newTab === undefined) {
      return;
    }
    newTab.isActive = true;
  }

  /**
   * Called upon activation of child tab's component, subscribes to its data emitter
   * and exposes its data to the current component through the tab's data property.
   * @param {any} componentRef - The reference to the child component.
   * @returns {void}
   */
  onActivateChild(componentRef: any): void {
    const emitter: EventEmitter<any> = componentRef.exposedData;

    this.childTabDataSub?.unsubscribe();
    this.childTabDataSub = emitter?.pipe(
      takeUntilDestroyed(componentRef.destroyRef))
      .subscribe((data) => {
        const currentTab = this.tabs.find(t => t.isActive);
        if (currentTab !== undefined) {
          currentTab.data = data;
        }
      });
  }
}
